import React from 'react';
import { CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import HelperMethods from '../../../services/exports/HelperMethods';

/**
 * The offer itself: plan name, the free-period badge, the headline price and
 * what the plan includes as check marks. Everything the partner is asked to
 * pay for is on this card; the steps around it only change what comes after.
 *
 * `trialDays` is the free period this restaurant is still owed — zero on an
 * add-on and on a restaurant that has used its trial — so the headline leads
 * with the free days only when the bill will actually start later.
 * `activeNote` replaces the price for a plan the restaurant already holds.
 *
 * `pricingTier` is the volume offer behind a tiered price (the Partner Plan):
 * below the threshold the card says what the rate drops to and when, at or
 * above it the card says the rate already is the volume one. Absent on an
 * add-on, a flat price, or a backend that predates tiered pricing.
 */
export default function PaywallPlanCard({
  product,
  productKey,
  price,
  trialDays = 0,
  activeNote = null,
  pricingTier = null,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:ProviderBilling:LicensePaywallModal' });

  const features = t(`products.${productKey}.features`, { returnObjects: true, defaultValue: {} });
  const featureList = features && typeof features === 'object' ? Object.values(features) : [];

  function renderPrice() {
    if (activeNote) {
      return (
        <p className="mb-none text-[15px] text-brand-text-default" data-testid="license-paywall-active">
          {activeNote}
        </p>
      );
    }

    return (
      <div className="flex flex-col gap-2xs">
        <div
          className="text-[34px] font-bold leading-[1.1] tracking-[-0.02em] text-brand-text-default"
          data-testid="license-paywall-headline"
        >
          {trialDays > 0 ? t('trial_headline', { days: trialDays }) : t('price_per_month', { price })}
        </div>
        <div className="text-[15px] text-brand-inkGrey-grey_3" data-testid="license-paywall-price">
          {trialDays > 0 ? t('then_price', { price }) : t('price_terms')}
        </div>
        {renderTier()}
      </div>
    );
  }

  function renderTier() {
    if (!pricingTier) {
      return null;
    }

    if (pricingTier.volume_active) {
      return (
        <span
          className="w-fit rounded-full bg-brand-inkGreen-green_0 px-[10px] py-[3px] text-xs font-semibold text-brand-inkGreen-green_2"
          data-testid="license-paywall-tier"
        >
          {t('tier_active', { threshold: pricingTier.threshold })}
        </span>
      );
    }

    return (
      <div className="text-sm text-brand-inkGrey-grey_3" data-testid="license-paywall-tier">
        {t('tier_upsell', {
          threshold: pricingTier.threshold,
          price: HelperMethods.formatCurrency(pricingTier.volume_amount, product.currency),
        })}
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-[18px] rounded-xl border border-solid border-brand-inkGrey-grey_2 p-[20px]"
      data-testid="license-paywall-plan"
    >
      <div className="flex items-center justify-between gap-sm">
        <span className="text-[15px] font-semibold text-brand-text-default">{product.name}</span>
        {trialDays > 0 && !activeNote && (
          <span
            className="whitespace-nowrap rounded-full bg-brand-inkGreen-green_0 px-[10px] py-[5px] text-xs font-bold uppercase text-brand-inkGreen-green_2"
            data-testid="license-paywall-trial-badge"
          >
            {t('trial_badge', { days: trialDays })}
          </span>
        )}
      </div>

      {renderPrice()}

      {featureList.length > 0 && (
        <>
          <div className="h-px bg-brand-inkGrey-grey_1" />
          <ul className="m-none flex list-none flex-col gap-[14px] p-none" data-testid="license-paywall-features">
            {featureList.map((feature) => (
              <li key={feature.title} className="flex items-start gap-sm">
                <CheckOutlined className="mt-[3px] shrink-0 text-[18px] text-brand-inkGreen-green_2" aria-hidden />
                <div className="flex flex-col gap-nano">
                  <span className="text-[15px] font-semibold text-brand-text-default">{feature.title}</span>
                  <span className="text-sm text-brand-inkGrey-grey_3">{feature.description}</span>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
